import { GetStaticPropsContext } from 'next';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { Breadcrumbs } from '~/components/breadcrumbs';
import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { CohortJourneySection } from '~/features/enterprise/landing-pages/components/cohort-journey-section';
import { EnterpriseLandingFeatureCardsSection } from '~/features/enterprise/landing-pages/components/enterprise-landing-feature-cards-section';
import { FooterBannerSection } from '~/features/enterprise/landing-pages/components/footer-banner-section';
import { ImpactSection } from '~/features/enterprise/landing-pages/components/impact-section';
import { SkillsIntelligenceSection } from '~/features/enterprise/landing-pages/components/skills-intelligence-section';
import cohortsConfig from '~/features/enterprise/landing-pages/configs/cohorts.json';
import { MessageIcon, OutcomesIcon, RocketIcon } from '~/features/enterprise/landing-pages/icons';
import { EnterpriseCohortsLandingPageConfig } from '~/features/enterprise/landing-pages/models/enterprise-platform-landing-page-config';
import { getEnterpriseLandingAnalyticsLabels } from '~/features/enterprise/landing-pages/utils/get-enterprise-landing-analytics-labels';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import {
  LogosCarousel,
  StaticLogosCarouselProps,
  transformStaticLogosCarouselProps,
} from '~/features/page-template/templates/enterprise-overview/components/logos-carousel';

type CohortsLandingPageProps = {
  config: EnterpriseCohortsLandingPageConfig;
  page: Page;
  staticLogosCarouselProps: StaticLogosCarouselProps;
};

export default function EnterpriseCohortsLandingPage({
  config,
  page,
  staticLogosCarouselProps,
}: CohortsLandingPageProps) {
  const { t } = useTranslation();
  const { hero, slug } = config;
  const tPrefix = `enterprise.${slug}.hero`;
  const analyticsLabels = getEnterpriseLandingAnalyticsLabels(slug);

  const breadcrumbs = config.breadcrumbs.map((bc) => ({
    label: t(`enterprise.${slug}.breadcrumbs.${bc.id}`),
    href: bc.href,
  }));

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <Breadcrumbs items={breadcrumbs} />
        <HeroLayout
          backgroundImageOverlayGradient='linear(90deg, #000 0%, rgba(0, 0, 0, 0) 46%, rgba(0, 0, 0, 0) 76%, #000 100%)'
          backgroundImageSlot={
            <>
              <Box display={{ base: 'block', md: 'none' }} inset={0} position='absolute'>
                <Image
                  alt=''
                  quality={100}
                  sizes='100vw'
                  src={hero.backgroundImage.mobile}
                  style={{
                    minWidth: '496px',
                    objectFit: 'cover',
                    objectPosition: '-100px -80px',
                    transform: 'scaleX(-1)',
                  }}
                  fill
                  priority
                />
              </Box>
              <Box bottom='-2px' display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
                <Image
                  alt=''
                  quality={100}
                  sizes='100vw'
                  src={hero.backgroundImage.desktop}
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'top right',
                    transform: 'scaleX(-1)',
                  }}
                  fill
                  priority
                />
              </Box>
            </>
          }
          ctaSlot={
            <>
              <ButtonLink
                buttonProps={{
                  flex: {
                    base: 1,
                    md: 'unset',
                  },
                  leftIcon: <RocketIcon height='20px' width='20px' />,
                  onClick: () =>
                    trackEvent({
                      name: 'Enterprise Platform CTA Clicked',
                      type: 'click',
                      label: analyticsLabels.demoCta,
                    }),
                }}
                linkProps={{
                  href: '/get-started/enterprise',
                }}
              >
                {t('enterprise.cohorts.cta.demoCohorts')}
              </ButtonLink>
              <ButtonLink
                buttonProps={{
                  colorScheme: 'white',
                  flex: {
                    base: 1,
                    md: 'unset',
                  },
                  leftIcon: <MessageIcon height='20px' width='20px' />,
                  variant: 'outline',
                  onClick: () =>
                    trackEvent({
                      name: 'Enterprise Platform CTA Clicked',
                      type: 'click',
                      label: analyticsLabels.footerContact,
                    }),
                }}
                linkProps={{
                  href: '/get-started/enterprise',
                }}
              >
                {t('enterprise.cohorts.cta.contactUs')}
              </ButtonLink>
            </>
          }
          minHeight={{
            md: '674px',
          }}
          textAndCtaContainerProps={{
            gap: {
              base: 6,
              md: '48px',
            },
          }}
          textContentSlot={
            <Flex
              flexDirection='column'
              whiteSpace='pre-wrap'
              gap={{
                base: 4,
                md: 8,
              }}
              marginTop={{
                base: '192px',
                md: 'unset',
              }}
              maxWidth={{
                base: '357px',
                md: '649px',
              }}
            >
              <Heading
                as='h1'
                color='white'
                fontWeight='500'
                fontSize={{
                  base: '3.25rem',
                  md: '4.75rem',
                }}
                lineHeight={{
                  base: '1.1',
                  md: '1',
                }}
              >
                {t(`${tPrefix}.title`)}
              </Heading>
              <Text color='gray.300' fontSize='lg' lineHeight='lg' maxWidth={{ base: '100%', md: '480px' }}>
                {t(`${tPrefix}.description`)}
              </Text>
            </Flex>
          }
        />
        <LogosCarousel {...staticLogosCarouselProps} />
        {config.skillsIntelligence && <SkillsIntelligenceSection config={config.skillsIntelligence} slug={slug} />}
        {config.impact && <ImpactSection config={config.impact} slug={slug} />}
        {config.benefits && (
          <EnterpriseLandingFeatureCardsSection
            slug={slug}
            config={{
              ...config.benefits,
              headerIcon: <OutcomesIcon color='grey.500' height='18px' width='18px' />,
            }}
          />
        )}
        {config.cohortJourney && <CohortJourneySection config={config.cohortJourney} slug={slug} />}
        <FooterBannerSection footerBanner={config.footerBanner} slug={slug} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  if (locale !== DEFAULT_LANGUAGE) {
    return { notFound: true };
  }

  const config = cohortsConfig as EnterpriseCohortsLandingPageConfig;

  const [page, staticLogosCarouselProps] = await Promise.all([
    getPage('/enterprise/overview/[slug]'),
    transformStaticLogosCarouselProps(),
  ]);

  if (!page) {
    return { notFound: true };
  }

  return {
    props: {
      page,
      config,
      staticLogosCarouselProps,
    },
  };
}
